import type { i18n as I18nInstance } from 'i18next';
import type { BasketTableAnswerItem, QuestionConfigQuestionType, QuestionDataValue } from '/index';

import { basketTableTotalTypes, questionScopeCategories } from '/business/shared/util/Statics';
import formatNumber from '/shared/functions/formatNumber';

import initiativeCardBackground from '/business/shared/images/initiatives/Business-travel-1x.jpg';
import initiativeBackground from '/business/shared/images/initiatives/Business-travel-2x.jpg';
import vehicleIcons from '/shared/util/vehicleIcons';

import ImageCard from '/business/shared/images/initiatives/Sustainable-Aviation-Fuel-card-image.jpg';
import ImageHeader from '/business/shared/images/initiatives/Sustainable-Aviation-Fuel-header.jpg';

import AirportCanonicalName from '/business/shared/components/widgets/air-travel/AirportCanonicalName';

type ConfigureCastrolAirSingularOptions = {
  hasScopeBasedOrganisation?: boolean;
  i18n: I18nInstance;
  reductionCardsSlides?: string[];
};

type AirTravelTableData = {
  airportFrom?: string | null;
  airportTo?: string | null;
  classificationOfTravel?: string | null;
  isReturn?: boolean | null;
  tonnage?: number | null;
};

function getNumberValue(value: QuestionDataValue): number {
  return typeof value === 'number' ? value : 0;
}

export const getConfigureCastrolAirSingular = ({
  i18n,
  hasScopeBasedOrganisation = true,
  reductionCardsSlides = ['slide1', 'slide2', 'slide3', 'slide4'],
}: ConfigureCastrolAirSingularOptions): QuestionConfigQuestionType => {
  const headings = [
    i18n.t('business:Questions.travel.question2.table.heading1'),
    i18n.t('business:Questions.travel.question2.table.heading2'),
    i18n.t('business:Questions.travel.question2.table.heading3'),
    'Travel class',

    i18n.t('business:Questions.travel.question2.table.heading4'),
  ];
  const cells = [
    ({ airportFrom, airportTo }: AirTravelTableData) => (
      <>
        <AirportCanonicalName airportCode={airportFrom} /> -
        <AirportCanonicalName airportCode={airportTo} />
      </>
    ),
    ({ isReturn }: AirTravelTableData) =>
      isReturn ? i18n.t('business:Snippets.returnFlight') : i18n.t('business:Snippets.oneWayFlight'),
    'numOfTripsPerYear',
    ({ classificationOfTravel }: AirTravelTableData) =>
      i18n.t(`business:flightTravelClasses.${classificationOfTravel}`),
    ({ tonnage }: AirTravelTableData) => `${formatNumber(tonnage)} ${i18n.t('business:Snippets.tonnes')}`,
  ];
  return {
    id: 'by-air',
    scopeCategory: hasScopeBasedOrganisation
      ? questionScopeCategories.SCOPE_3
      : questionScopeCategories.TRAVEL_EMISSIONS,
    title: i18n.t('business:Questions.travel.question2.title'),
    isCompletePropName: 'isBusinessTravelComplete',
    apiKey: 'calculation-travel-emissions-business-travel',
    primaryI18nKey: 'business:QuestionPage.forms.travelEmissionsSelector.para5',
    subTextI18nKey: 'business:QuestionPage.forms.travelEmissionsSelector.para6',
    overviewPageTitleI18nKey: 'business:Questions.travel.question2.title',
    icon: vehicleIcons.airPlane,
    reductionPageKey: 'businessTravel',
    initiative: {
      getVisuals: ({ isBpOffering }) => {
        if (!isBpOffering) {
          return {
            description: false,
            background: initiativeBackground,
            useMask: false,
            cardBackground: initiativeCardBackground,
          };
        }
        if (isBpOffering) {
          return {
            description: false,
            background: ImageHeader,
            useMask: false,
            cardBackground: ImageCard,
          };
        }
      },
      formatForInitiativeSummary: () => {
        return {
          isShowingReportingPeriod: true,
          isShowingReportingPeriodNoReduction: false,
          category: false,
          description: 'Number of trips during the reporting period ',
          descriptionNoReduction: false,
        };
      },
    },
    reductionPageCards: reductionCardsSlides,
    form: import('/business/shared/components/questions/scope3/businessTravel/OutOfSys_Air'),
    table: {
      title: i18n.t('business:Questions.travel.question2.table.title'),
      calculateTotal: (allItems: BasketTableAnswerItem[]) => {
        return allItems.reduce((accumulator, item) => {
          const numOfTrips = getNumberValue(item?.answer?.genericData?.numOfTripsPerYear);
          if (numOfTrips > 0) {
            return accumulator + numOfTrips;
          }
          return accumulator;
        }, 0);
      },
      totalType: basketTableTotalTypes.COUNT,
      headings,
      cells,
      isAnswerEditable: false,
      supplier: {
        columns: ['emissionSource', 'supplierName', 'numberOfTrips', 'totalDistance', 'carbonEmissions'],
      },
    },
    details: {},
  };
};
